/**
 * Source drawer — the pure half of the table body. Fork-owned (zone C).
 *
 * The demo's `audit()` (line 1910) re-renders its table on every keystroke in
 * the "Filter rows…" box: it keeps the rows whose JSON contains the query,
 * case-insensitive, and renders the first 400 with a note when there are more.
 * These functions are that `render(q)` closure without the HTML.
 *
 * Transformation layer — no React, no side effects.
 *
 * @see src/versions/v1/chrome-oem/SourceDrawerBody.tsx
 */

/** Demo line 1910. The drawer never renders more than 400 rows. */
export const SOURCE_ROW_LIMIT = 400;

/** A payload cell. */
export type SourceCell = string | number | null;

/** One row as the table renders it: the cells plus its index in the payload. */
export interface VisibleRow {
  /** Index into `payload.rows` — stable across filtering, used for highlight and key. */
  readonly index: number;
  readonly cells: readonly SourceCell[];
  readonly highlighted: boolean;
}

/** What the table shows for one query. */
export interface VisibleRows {
  readonly rows: readonly VisibleRow[];
  /** Rows that matched the query, before the 400 cap. */
  readonly matched: number;
  /** `true` when the cap cut rows off — the body then prints the demo's note. */
  readonly capped: boolean;
}

/**
 * The lower-cased text each row is searched against, built once per payload.
 * The demo searched `JSON.stringify(row)`; joining the cells finds the same
 * values without also matching on the column names.
 */
export function rowHaystacks(rows: readonly (readonly SourceCell[])[]): string[] {
  return rows.map((row) => row.map((cell) => (cell == null ? '' : String(cell))).join('\u0001').toLowerCase());
}

/**
 * visibleRows(rows, haystacks, query, highlight) → VisibleRows
 * Projection rule: filter on the query (empty query keeps everything), then
 *   cap at {@link SOURCE_ROW_LIMIT}. Highlighting marks rows; it never filters.
 */
export function visibleRows(
  rows: readonly (readonly SourceCell[])[],
  haystacks: readonly string[],
  query: string,
  highlight: ReadonlySet<number>,
): VisibleRows {
  const q = query.trim().toLowerCase();
  const out: VisibleRow[] = [];
  let matched = 0;
  rows.forEach((cells, index) => {
    if (q && !haystacks[index].includes(q)) return;
    matched += 1;
    if (out.length < SOURCE_ROW_LIMIT) out.push({ index, cells, highlighted: highlight.has(index) });
  });
  return { rows: out, matched, capped: matched > SOURCE_ROW_LIMIT };
}

/** Demo line 1910. A cell as printed: one decimal on a fraction, `—` for nothing. */
export function formatCell(cell: SourceCell): string {
  if (cell == null || cell === '') return '—';
  if (typeof cell === 'number' && !Number.isInteger(cell)) return cell.toFixed(1);
  return String(cell);
}
